/**
 * DIALER RESET ("Refresh Dialler") — header button that unblocks a stuck dialer.
 * ONE button that solves ALL dialer problems (Shailendra 2026-08-27).
 *
 * Two failure modes it fixes:
 *  1. STUCK SOFTPHONE LINE — Phonomenal leaves the member in offering/on_call when
 *     the call-end webhook is late/lost; every dial then rejects "member on another
 *     call" though the softphone shows "Waiting for Call".
 *  2. WEDGED QUEUE — the queue parks in a system pause (dial_rejected /
 *     session_error / softphone_offline), a phantom awaiting_disposition, a stale
 *     cooldown, or a dead in-flight call, so the cockpit freezes and Resume loops.
 *
 * POST /api/ai/voice/end-call now does BOTH: recoverQueue() un-wedges the queue
 * (never touching an intentional pause or a live call), then the line reset
 * disposes the call and — if the member is still stuck — ends the session (the
 * only thing that reliably resets it). Fires a 'dialer:refresh' window event so
 * My Chats re-polls and the cockpit updates immediately.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button, Tooltip, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PhonelinkEraseIcon from '@material-ui/icons/PhonelinkErase';

// .trim() — the shared .env value has a stray leading space.
const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();

export default function DialerResetButton() {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const reset = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${AI_PATH}/api/ai/voice/end-call`, {});
      const recovered = r.data?.queueRecovered?.recovered;
      setToast({
        open: true,
        severity: 'success',
        message: recovered
          ? 'Dialer unblocked — the next number will dial. Log in on the VOIZ softphone if it asks.'
          : r.data?.hardReset
            ? 'Line freed — your session was reset. Log in again on the VOIZ softphone, then call.'
            : 'Line is free. You can place your next call.',
      });
      // Tell My Chats to re-poll the dialer NOW so the pane updates immediately
      // instead of waiting for the next 15s heartbeat.
      try { window.dispatchEvent(new CustomEvent('dialer:refresh')); } catch (e) { /* noop */ }
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.response?.data?.message || 'Could not reset the dialer — try again in a moment.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Labelled, not a bare icon — an agent who is blocked mid-shift should be
          able to READ what this does without hovering or guessing. */}
      <Tooltip title="Stuck on a call and unable to dial? This frees your line.">
        <span>
          <Button
            onClick={reset}
            disabled={busy}
            variant="outlined"
            startIcon={<PhonelinkEraseIcon />}
            style={{
              marginRight: 12,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              padding: '6px 14px',
              borderRadius: 8,
              color: busy ? '#999' : '#e65100',
              borderColor: busy ? '#ddd' : '#ffb74d',
              background: busy ? '#f5f5f5' : '#fff4e5',
              whiteSpace: 'nowrap',
            }}
          >
            {busy ? 'Refreshing…' : 'Refresh Dialler'}
          </Button>
        </span>
      </Tooltip>
      <Snackbar open={toast.open} autoHideDuration={7000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
